import {
    DynamicListAddButton,
    DynamicListWrapperProps,
    ValidatedDropDownList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput
} from '@finbeat/react-toolkit';
import { TariffsInfo } from 'models/api/tariff';
import { useTariffInfoState } from 'providers/TariffInfoProvider';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Table } from 'reactstrap';

const TariffsServiceFormWrapper = ({ children, disabled, ...props }: DynamicListWrapperProps) => {
    const { t } = useTranslation();
    return (
        <Table bordered>
            <thead>
                <tr className="align-middle">
                    <th style={{ width: '18.23%' }}>{t('BackOfficeService')}</th>
                    <th style={{ width: '33.3%' }}>{t('BackOfficeFormula')}</th>
                    <th style={{ width: '18.23%' }}>{t('BackOfficeType')}</th>
                    <th style={{ width: '18.23%' }}>{t('BackOfficeTypeOfCalculation')}</th>
                    <th style={{ width: '12%' }}>{'Idx'}</th>
                    {!disabled && (
                        <th className="w-10 text-center">
                            <DynamicListAddButton {...props} />
                        </th>
                    )}
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </Table>
    );
};

type TariffsServiceFormProps = ValidatedDynamicListFormComponentProps & {
    periodIdx: number;
    parentServiceIdx: number;
};

const TariffsServiceForm = ({
    periodIdx,
    parentServiceIdx,
    idx,
    disabled,
}: TariffsServiceFormProps) => {
    const { dicts, dictsLoading, service, serviceLoading } = useTariffInfoState();
    const { watch } = useFormContext<TariffsInfo>();

    const serviceId = watch(
        `periods.${periodIdx}.parentServices.${parentServiceIdx}.services.${idx}.serviceId`
    );
    const accessType = watch('accessType');
    const editableFields = watch('editableFields');
    const isEditable = useMemo(
        () =>
            accessType === 'FullAccess' ||
            (accessType === 'PartialAccess' && editableFields?.some((x) => x === serviceId)),
        [serviceId, accessType, editableFields]
    );

    return (
        <>
            <td>
                <ValidatedDropDownList<TariffsInfo>
                    name={`periods.${periodIdx}.parentServices.${parentServiceIdx}.services.${idx}.serviceId`}
                    isFloating={false}
                    isClearable={false}
                    items={service}
                    isLoading={serviceLoading}
                    disabled={disabled}
                />
            </td>
            <td>
                <ValidatedInput<TariffsInfo>
                    name={`periods.${periodIdx}.parentServices.${parentServiceIdx}.services.${idx}.formula`}
                    type="textarea"
                    isFloating={false}
                    disabled={!isEditable}
                />
            </td>
            <td>
                <ValidatedDropDownList<TariffsInfo>
                    name={`periods.${periodIdx}.parentServices.${parentServiceIdx}.services.${idx}.serviceType`}
                    isFloating={false}
                    isClearable={false}
                    items={dicts.serviceTypes}
                    isLoading={dictsLoading}
                    disabled={!isEditable}
                />
            </td>
            <td>
                <ValidatedDropDownList<TariffsInfo>
                    name={`periods.${periodIdx}.parentServices.${parentServiceIdx}.services.${idx}.calcType`}
                    isFloating={false}
                    isClearable={false}
                    items={dicts.calcTypes}
                    isLoading={dictsLoading}
                    disabled={!isEditable}
                />
            </td>
            <td>
                <ValidatedInput<TariffsInfo>
                    name={`periods.${periodIdx}.parentServices.${parentServiceIdx}.services.${idx}.idx`}
                    isFloating={false}
                    disabled={!isEditable}
                />
            </td>
        </>
    );
};

export { TariffsServiceForm, TariffsServiceFormWrapper };
